/**
 * SupportIcons — compact contact-icon row.
 *
 * Used in the client header (top bar) next to the news bell.
 * Bottom-right is reserved for the Chatway widget, so this no longer
 * floats — it lives inline alongside Queue / News / Balance.
 *
 * Contact info — change here to update everywhere:
 *   email     info@auto-remap.com
 *   whatsapp  +34 692 03 30 56  (wa.me/34692033056)
 *   telegram  +34 692 03 30 56  (t.me/+34692033056)
 *   wechat    https://msngr.link/wc/auto_remap_cn
 */
import React, { useState } from 'react';
import { Mail, MessageCircle, Send, Check } from 'lucide-react';

const EMAIL = 'info@auto-remap.com';
const PHONE = '+34692033056';

const CONTACTS = [
  { id: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/${PHONE.replace('+', '')}`, tone: 'bg-emerald-500 hover:bg-emerald-400', icon: MessageCircle },
  { id: 'telegram', label: 'Telegram', href: `https://t.me/${PHONE}`,                   tone: 'bg-sky-500 hover:bg-sky-400',          icon: Send },
  { id: 'wechat',   label: 'WeChat',   href: 'https://msngr.link/wc/auto_remap_cn',     tone: 'bg-green-600 hover:bg-green-500',     icon: WeChatGlyph },
];

/** Minimal WeChat glyph — lucide-react has no WeChat icon. */
function WeChatGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M9 4C4.58 4 1 6.91 1 10.5c0 2 1.13 3.78 2.9 4.97L3 18l3.06-1.55c.62.13 1.27.2 1.94.2.16 0 .31 0 .47-.02-.21-.62-.34-1.27-.34-1.95 0-3.31 3.13-6 7-6 .25 0 .5.01.74.04C15.27 6.04 12.43 4 9 4zm-3 4.5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm6 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm4 2.4c-3.31 0-6 2.24-6 5 0 2.76 2.69 5 6 5 .56 0 1.11-.06 1.62-.18L20 22l-.78-2.21c1.11-.92 1.78-2.18 1.78-3.59 0-2.76-2.69-5-5-5zm-2 2.6c.41 0 .75.34.75.75s-.34.75-.75.75-.75-.34-.75-.75.34-.75.75-.75zm4 0c.41 0 .75.34.75.75s-.34.75-.75.75-.75-.34-.75-.75.34-.75.75-.75z"/>
    </svg>
  );
}

export default function SupportIcons() {
  // "Copied!" feedback on the email button — covers the case where the user
  // doesn't have a default email client wired up (mailto: silently does
  // nothing in many browsers). Clipboard always works as a fallback.
  const [copied, setCopied] = useState(false);

  const onMailClick = (e) => {
    // We let the default mailto: navigation happen but ALSO drop the address
    // into the clipboard so the user has a manual fallback if no handler is
    // installed (Chrome/Edge on a fresh machine).
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(EMAIL);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (_) { /* silent */ }
    // Don't preventDefault — we want the user's mail client to open if
    // configured.
  };

  return (
    <div className="flex items-center gap-1.5" data-testid="support-icons-root">
      {/* Email — pseudo-button: tries mailto and copies to clipboard. */}
      <a
        href={`mailto:${EMAIL}`}
        onClick={onMailClick}
        data-testid="support-icon-email"
        title={copied ? 'Email copied!' : `Email · ${EMAIL}`}
        className="relative w-9 h-9 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95"
      >
        {copied ? <Check className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
        {copied && (
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded shadow-lg whitespace-nowrap pointer-events-none">
            Copied!
          </span>
        )}
      </a>
      {CONTACTS.map(({ id, label, href, tone, icon: Icon }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noreferrer"
          data-testid={`support-icon-${id}`}
          title={label}
          className={`w-9 h-9 rounded-full text-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${tone}`}
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
}

